import {
  useBoolean,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Flex,
  Text,
  Box,
  Table,
  Thead,
  Tr,
  Tbody,
  Td,
  Th,
  Tfoot
} from '@chakra-ui/react'
import * as crypto from 'crypto-js'
import fileDownload from 'js-file-download'
import {
  createContext,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { AiOutlineBarChart } from 'react-icons/ai'
import Swal from 'sweetalert2'
import { useImmer } from 'use-immer'

import useSessions from '@hook/shared/use-sessions'

import LayoutLoader from '@component/Layout/Loader'
import SapHistoricModalWrapper from '@component/Layout/Tools/SapHistoricModalWrapper'
import MrpPeriodChart from '@component/Modules/Mrp/Modals/PeriodChart'

import { getIsUsuarioCompras } from '@helper/auth/getUserTokenClaims'
import { parsePurchaseMrpData } from '@helper/grid'
import { getErrorMessage } from '@helper/mrp/errorMessage'
import { AlteredSku, upsertAlteredSku } from '@helper/mrp/justifications'
import paramsHelper from '@helper/params'
import { mergeStoredHeaders } from '@helper/util/cryptoStorange'
import generic from '@service/generic'
import mrp from '@service/mrp'

import {
  ITableGridAction,
  ITableHeader
} from '@base/@types/components/Layout/TableGrid'
import { ILayoutToolsAction } from '@base/@types/components/Layout/Tools'
import {
  IHeaderLocalStorange,
  IMrpPurchaseContext,
  IParams,
  IParamsSap,
  IUpdateParams,
  MrpPurchaseCalc,
  Pagination,
  Discount,
  IUpdateParamsSap,
  bufferObj,
  bufferFooter
} from '@base/@types/providers/Mrp/PurchaseProvider'
import mrpPurchaseHeader from '@base/variables/headers/mrp/purchase'
import { unitToInt } from '@base/variables/shared/sap'

// Suporta tanto objeto {value} (react-select isMulti=false) quanto array [{value}] (estado inicial/localStorage)
const getUtilizarCronogramaValue = (cron: any): string | undefined => {
  if (!cron) return undefined
  if (Array.isArray(cron)) return cron[0]?.value
  return cron.value
}

// Monta o SKU alterado a partir do item da recomendação (referência = quantidade recomendada)
const buildPurchaseAlteredSku = (item: any) => {
  const novaQuantidade = Number(item?.desejada?.quantidade ?? 0)
  return {
    id: item.estoque.id,
    loja: item.estoque.loja,
    produto: item.estoque.produtoFornecedor?.produto,
    idEmpresa: item.estoque.idEmpresa,
    quantidadeOriginal: Number(item?.valorRecomendacao?.quantidade ?? 0),
    novaQuantidade,
    acimaToleranciaPermitida: item.acimaToleranciaPermitida || false,
    valor: novaQuantidade * (item.estoque.custoUnitario || 0)
  }
}

const MrpPurchaseProvider: FC = ({ children }) => {
  const toast = useToast()
  const { register } = useSessions()
  // Somente o usuário compras trabalha com o Cronograma de Compras
  const isUsuarioCompras = useMemo(() => getIsUsuarioCompras(), [])

  const [loading, setLoading] = useState(false)
  const [loadingExport, setLoadingExport] = useState(false)
  const [fetching, setFetching] = useState(false)
  const [filters, setFilters] = useBoolean(true)

  const [query, setQuery] = useState('')
  const [filename, setFilename] = useState('MRP Compra')

  const [selected, setSelected] = useState<any>()
  const [sapFlag, setSapFlag] = useState<boolean | null>(null)
  const [vmmFlag, setVmmFlag] = useState<boolean | null>(null)
  const [lastSearchVmm, setLastSearchVmm] = useState(false)

  const [possuirBuffer, updatePossuirBuffer] = useImmer<any[]>([])

  const [buffers, updateBuffers] = useImmer<bufferObj[]>([])
  const [centroBuffer, updateCentroBuffer] = useImmer<bufferObj>({})
  const [bufferFooter, updateBufferFooter] = useImmer<bufferFooter>({
    disponivel: 0,
    reservado: 0
  })
  const [loadingBufferModal, setLoadingBufferModal] = useState(false)
  const [bufferModalOpen, setBufferModalOpen] = useState(false)
  const [bufferModalCentroId, setBufferModalCentroId] = useState<number>(0)
  const [bufferModalProdutoId, setBufferModalProdutoId] = useState<number>(0)

  const [data, updateData] = useImmer<any[]>([])
  // Espelhado durante o render: callbacks assíncronos (ex.: PUT da quantidade)
  // leem sempre o estado mais recente, sem closure desatualizada
  const dataRef = useRef<any[]>(data)
  dataRef.current = data
  const [supplyMethod, updateSupplyMethod] = useImmer<any[]>([])
  const [discountSupply, updateDiscountSupply] = useImmer<Discount>({
    id: 0,
    desconto: 0,
    fornecedorId: 0,
    metodoPagamentoId: 0
  })
  const [pagination, updatePagination] = useImmer<Pagination>({
    actualPage: 0,
    rows: 0,
    totalPages: 0,
    totalRows: 0,
    skip: 0
  })
  const [dataCards, updateDataCards] = useImmer<MrpPurchaseCalc>({
    skusDistintos: 0,
    volume: 0,
    custo: 0,
    custoRecomendado: 0,
    cards: []
  })

  const [calc, _updateCalc] = useImmer<MrpPurchaseCalc>({
    skusDistintos: 0,
    volume: 0,
    custo: 0,
    custoRecomendado: 0,
    cards: []
  })

  const [params, updateParams] = useImmer<IParams>({
    categoriasIds: [],
    subCategoriaSelectIds: [],
    categoriasCompactasIds: [],
    produtosIds: [],
    fornecedoresIds: [],
    gerenteCategoriaIds: [],
    gerenteProdutoIds: [],
    mundoIds: [],
    lojasIds: [],
    curvaAbc: [],
    tiposAlertas: [],
    curvaIgualZeroRadio: '',
    quantidadeEstoqueRadio: '',
    utilizarVendaMediaMovel: 'Média Móvel',
    centerTypes: [],
    utilizarCronograma: [
      {
        label: 'Sim',
        value: '1'
      }
    ]
  })

  const [currentOrderCode, setCurrentOrderCode] = useState('')
  const [orderSuccessMessage, setOrderSuccessMessage] = useState('')
  const [appliedParams, setAppliedParams] = useState<IParams | null>(null)
  const [alteredSkus, setAlteredSkus] = useImmer<AlteredSku[]>([])
  const alteredSkuIds = useMemo(
    () => new Set(alteredSkus.map(sku => sku.id)),
    [alteredSkus]
  )
  // Trava contra envio duplicado do pedido ao SAP
  const isSubmittingRef = useRef(false)
  const [sendingOrder, setSendingOrder] = useState(false)
  // Quantidade desejada em envio por SKU (evita PUT duplicado de Enter + blur)
  const pendingWantedRef = useRef(
    new Map<number, { quantidade: number; original: any }>()
  )
  // Justificativa única do Comprador para não utilizar o Cronograma de Compras
  // (registrada no pedido; não exige aprovação por si só)
  const [justificativaForaCronograma, setJustificativaForaCronograma] =
    useState('')

  const handleClearOrderCode = () => {
    setCurrentOrderCode('')
    setOrderSuccessMessage('')
  }
  const filtersList = useMemo<any[]>(() => {
    const data = [
      // O filtro de cronograma só se aplica ao usuário compras
      ...(isUsuarioCompras
        ? [
            {
              id: 'utilizarCronograma',
              label: 'Utilizar Cronograma',
              filterId: 'utilizarCronograma',
              type: 'select',
              isMulti: false
            }
          ]
        : []),
      ...(vmmFlag
        ? [
            {
              id: 'utilizarVendaMediaMovel',
              label: 'Projeção',
              type: 'radio',
              values: ['Lambda', 'Média Móvel']
            }
          ]
        : []),
      {
        id: 'centerTypes',
        label: 'Tipo de Centro',
        filterId: 'centerTypes',
        type: 'select',
        isMulti: true
      },
      {
        id: 'lojasIds',
        label: 'Centro',
        filterId: 'centers',
        type: 'select',
        isMulti: true
      },
      {
        id: 'gerenteProdutoIds',
        label: 'Gerente de Produto',
        filterId: 'productManagers',
        type: 'select',
        isMulti: true,
        isDisabled: params.produtosIds?.length > 0
      },
      {
        id: 'categoriasIds',
        label: 'Categoria',
        filterId: 'categories',
        type: 'select',
        isMulti: true
      },
      {
        id: 'categoriasCompactasIds',
        label: 'Categoria Compacta',
        filterId: 'compactCategories',
        type: 'select',
        isMulti: true
      },
      {
        id: 'subCategoriaSelectIds',
        label: 'Sub Categoria',
        filterId: 'subCategories',
        type: 'select',
        isMulti: true
      },
      {
        id: 'produtosIds',
        label: 'Produto',
        type: 'select-api-post',
        apiUrl: '/v1/Produto/Compras',
        isMulti: true,
        isDisabled: params.fornecedoresIds?.length > 0
      },
      {
        id: 'fornecedoresIds',
        label: 'Fornecedor',
        filterId: 'providersForPurchase',
        type: 'select',
        isMulti: true,
        isDisabled: params.produtosIds?.length > 0
      },
      {
        id: 'curvaAbc',
        label: 'Curva',
        filterId: 'abcCurves',
        isMulti: true,
        type: 'select',
        limit: 1
      },
      {
        id: 'tiposAlertas',
        label: 'Tipo de Alerta',
        filterId: 'typeOfAlert',
        isMulti: true,
        type: 'select'
      },
      {
        id: 'curvaIgualZeroRadio',
        label: 'Sugerida',
        type: 'radio',
        values: ['> 0', '= 0']
      },
      {
        id: 'quantidadeEstoqueRadio',
        label: 'Quantidade de Estoque',
        type: 'radio',
        values: [' > 0', ' = 0']
      }
    ]

    return data
  }, [params, vmmFlag, isUsuarioCompras])
  // A configuração salva entra já na inicialização do estado. Restaurá-la
  // depois, em efeito ou durante a busca, descartava a seleção que o usuário
  // tinha acabado de aplicar.
  const [headers, updateHeaders] = useImmer<ITableHeader[]>(() => {
    try {
      const stored = localStorage.getItem('mrpPurshaseMrp')

      if (!stored) return mrpPurchaseHeader

      return mergeStoredHeaders(
        mrpPurchaseHeader,
        JSON.parse(
          crypto.AES.decrypt(stored, 'CHAVE').toString(crypto.enc.Utf8) || ''
        )
      )
    } catch {
      // Configuração corrompida: mantém o padrão.
      return mrpPurchaseHeader
    }
  })

  const [headerLocalStorange, _updateHeaderLocalStorange] =
    useImmer<IHeaderLocalStorange>({
      name: 'mrpPurchase',
      header: mrpPurchaseHeader
    })

  const [paramsSap, updateSapParams] = useImmer<IParamsSap>({
    desconto: '',
    metodoPagamento: [],
    QuantidadeMaximaItensPedido: ''
  })

  const handleUpdateSapParams = useCallback(
    ({ name, value }: IUpdateParamsSap) => {
      updateSapParams(draft => {
        draft[name] = value
        if (
          name === 'metodoPagamento' &&
          discountSupply.metodoPagamentoId === value[0]?.value
        ) {
          // Forma de pagamento padrão do fornecedor: aplica o desconto cadastrado
          draft.desconto = discountSupply.desconto.toString()
        }
        if (
          name === 'desconto' &&
          draft.metodoPagamento[0]?.value ===
            discountSupply.metodoPagamentoId &&
          Number(value) < Number(discountSupply.desconto)
        ) {
          // Desconto mínimo para a forma de pagamento padrão do fornecedor
          draft.desconto = discountSupply.desconto.toString()
        }
      })
    },
    [discountSupply, updateSapParams]
  )

  const isSapExportFlag = async () => {
    try {
      const { data: res } = await generic.getConfig('FLAG_EXPORTAR_SAP_COMPRA')
      const { data } = res
      if (data?.valor === 'true') {
        return true
      } else {
        return false
      }
    } catch {
      return false
    }
  }

  const isVmmFlag = async () => {
    try {
      const { data: res } = await generic.getConfig('FLAG_VENDAMEDIAMOVEL')
      const { data } = res
      return data?.valor === 'true'
    } catch {
      return false
    }
  }

  const saveHeadersTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  )

  const saveHeadersToStorage = useCallback((hdrs: ITableHeader[]) => {
    if (saveHeadersTimeoutRef.current) {
      clearTimeout(saveHeadersTimeoutRef.current)
    }
    saveHeadersTimeoutRef.current = setTimeout(() => {
      localStorage.setItem(
        'mrpPurshaseMrp',
        crypto.AES.encrypt(JSON.stringify(hdrs), 'CHAVE').toString()
      )
    }, 300)
  }, [])

  const toggleColumn = useCallback(
    (index: number, column: number) => {
      updateHeaders(draft => {
        const col = draft[index]?.columns?.[column]
        if (col) {
          col.show = !col.show
          // Marca a coluna como configurada pelo usuário. Não pode alternar
          // junto com `show`, senão o editor perde o grupo de vista.
          col.notShowColumn = true
        }
      })
    },
    [updateHeaders]
  )

  // Persiste a configuração sempre que ela muda. Na montagem regrava o mesmo
  // valor que acabou de ser restaurado, então é inofensivo.
  useEffect(() => {
    if (headers && headers.length > 0) {
      saveHeadersToStorage(headers)
    }
  }, [headers, saveHeadersToStorage])

  const getOrderDate = useCallback((leadTime: number) => {
    const today = new Date()
    const newdate = new Date()
    newdate.setDate(today.getDate() + leadTime)
    const day =
      newdate.getDate() < 10 ? `0${newdate.getDate()}` : `${newdate.getDate()}`
    const month =
      newdate.getMonth() < 9
        ? `0${newdate.getMonth() + 1}`
        : `${newdate.getMonth() + 1}`
    return `${day}.${month}.${newdate.getFullYear()}`
  }, [])

  const sapExport = useCallback(() => {
    const arr: any[] = []

    for (const item of data) {
      let desiredQuantity =
        item.desejada.quantidade *
        item.estoque.produtoFornecedor.unidadeFornecedor.fatorConversao

      const unitMeasureSupplier =
        item.estoque.produtoFornecedor.unidadeFornecedor.nome

      if (unitToInt.includes(unitMeasureSupplier)) {
        desiredQuantity = parseInt(
          (Math.round(desiredQuantity * 100) / 100).toFixed()
        )
      }

      const orderType = Number(item.type) === 0 ? 'NB' : 'UB'

      arr.push({
        'codigo-material': item.estoque.produtoFornecedor.produto
          ? item.estoque.produtoFornecedor.produto.id
          : 0,
        'codigo-loja': item.estoque.loja ? item.estoque.loja.id : 0,
        'codigo-fornecedor': item.estoque.produtoFornecedor.fornecedor
          ? item.estoque.produtoFornecedor.fornecedor.id
          : 0,
        'quantidade-desejada': desiredQuantity
          .toLocaleString('pt-BR', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2
          })
          .replace('.', ''),
        'tipo-pedido': orderType,
        'tipo-data': 'T',
        'data-remessa': getOrderDate(item.estoque.tempoDeEspera)
      })
    }

    return arr
  }, [getOrderDate, data])
  // Espelhado durante o render (e não em um efeito): as actions `columns` e
  // `export` leem este ref por getter, e um efeito as deixaria um render
  // atrasadas — mostrando e exportando a seleção de colunas anterior.
  const headersRef = useRef(headers)
  headersRef.current = headers

  const actions = useMemo<ILayoutToolsAction[]>(() => {
    if (sapFlag === null) return []
    return [
      {
        type: 'purchaseSuppliers-createOrder',
        data: data,
        onUpdate: null
      },
      {
        type: 'export',
        exports: [
          {
            type: 'excel' as const,
            entity: 'purshaseMrp',
            data,
            onClick: sapExport,
            get headers() {
              return headersRef.current
            },
            loading: loadingExport
          },
          ...(sapFlag
            ? [
                {
                  type: 'sap' as const,
                  data,
                  sapModal: true,
                  loading: loadingExport
                }
              ]
            : [])
        ]
      },
      {
        type: 'purchaseUpload'
      },
      {
        type: 'purchaseSuppliers',
        data: data,
        onUpdate: updateData
      },
      {
        type: 'columns',
        get data() {
          return headersRef.current
        },
        onToggleColumn: toggleColumn
      }
    ]
  }, [sapFlag, data, sapExport, loadingExport, updateData, toggleColumn])

  const tableInteraction = useMemo<ITableGridAction[]>(() => {
    return [
      {
        type: 'icon',
        icon: <AiOutlineBarChart />,
        onSelect: setSelected
      },
      {
        type: 'iconCalculo'
      },
      {
        type: 'iconBuffer',
        icon: <AiOutlineBarChart />
      }
    ]
  }, [])

  const clearAllFilters = useCallback(() => {
    updateParams({
      categoriasIds: [],
      categoriasCompactasIds: [],
      produtosIds: [],
      fornecedoresIds: [],
      gerenteCategoriaIds: [],
      gerenteProdutoIds: [],
      mundoIds: [],
      lojasIds: [],
      curvaAbc: [],
      tiposAlertas: [],
      subCategoriaSelectIds: [],

      curvaIgualZeroRadio: '',
      quantidadeEstoqueRadio: '',
      utilizarVendaMediaMovel: 'Média Móvel',
      centerTypes: [],
      utilizarCronograma: [
        {
          label: 'Sim',
          value: '1'
        }
      ]
    })
    if (localStorage.getItem('mrpPurchaseParams')) {
      localStorage.setItem(
        'mrpPurchaseParams',
        crypto.AES.encrypt(
          JSON.stringify({
            categoriasIds: [],
            categoriasCompactasIds: [],
            produtosIds: [],
            fornecedoresIds: [],
            gerenteCategoriaIds: [],
            gerenteProdutoIds: [],
            mundoIds: [],
            lojasIds: [],
            curvaAbc: [],
            tiposAlertas: [],
            curvaIgualZeroRadio: '',
            quantidadeEstoqueRadio: '',
            centerTypes: [],
            utilizarCronograma: [
              {
                label: 'Sim',
                value: '1'
              }
            ]
          }),
          'CHAVE'
        ).toString()
      )
    } else {
      localStorage.setItem(
        'mrpPurchaseParams',
        crypto.AES.encrypt(
          JSON.stringify({
            ...params,
            categoriasIds: [],
            categoriasCompactasIds: [],
            produtosIds: [],
            fornecedoresIds: [],
            gerenteCategoriaIds: [],
            gerenteProdutoIds: [],
            mundoIds: [],
            lojasIds: [],
            curvaAbc: [],
            tiposAlertas: [],
            curvaIgualZeroRadio: '',
            quantidadeEstoqueRadio: '',
            centerTypes: [],
            utilizarCronograma: [
              {
                label: 'Sim',
                value: '1'
              }
            ]
          }),
          'CHAVE'
        ).toString()
      )
    }
  }, [params, updateParams])

  const handleUpdateParams = useCallback(
    ({ name, value }: IUpdateParams) => {
      updateParams(draft => {
        draft[name] = value
      })
      if (localStorage.getItem('mrpPurchaseParams')) {
        const newParams: IParams[] = JSON.parse(
          crypto.AES.decrypt(
            localStorage.getItem('mrpPurchaseParams'),
            'CHAVE'
          ).toString(crypto.enc.Utf8) || ''
        )

        const updatedParams = { ...newParams, [name]: value }

        localStorage.setItem(
          'mrpPurchaseParams',
          crypto.AES.encrypt(JSON.stringify(updatedParams), 'CHAVE').toString()
        )
      }
    },
    [updateParams]
  )

  const handleLoading = useCallback(() => {
    setLoading(true)
  }, [])
  const startBackgroundPagination = useCallback(
    async (page: number, params: any) => {
      try {
        const { data: res } = await mrp.getPurschaseData({
          page,
          params
        })

        const { data, pagination } = res

        updateData(draft => {
          return draft.concat(data)
        })

        if (pagination.totalPages === page) {
          setFetching(false)
        } else {
          startBackgroundPagination(page + 1, params)
        }
      } catch {}
    },
    [updateData]
  )

  const getDataCards = useCallback(async () => {
    setLoading(true)

    try {
      const paramsToSend = {
        ...params,
        categoriasIds: paramsHelper.parse(params.categoriasIds),
        categoriasCompactasIds: paramsHelper.parse(
          params.categoriasCompactasIds
        ),
        produtosIds: paramsHelper.parse(params.produtosIds),
        fornecedoresIds: paramsHelper.parse(params.fornecedoresIds),
        gerenteProdutoIds: paramsHelper.parse(params.gerenteProdutoIds),
        gerenteCategoriaIds: paramsHelper.parse(params.gerenteCategoriaIds),
        mundoIds: paramsHelper.parse(params.mundoIds),
        lojasIds: paramsHelper.parse(params.lojasIds),
        curvaAbc: params.curvaAbc.length > 0 ? params.curvaAbc[0].value : '',
        tiposCentros: paramsHelper.parse(params.centerTypes),
        utilizarCronogramaCompras:
          isUsuarioCompras &&
          getUtilizarCronogramaValue(params.utilizarCronograma) === '1',
        quantidadeDesejadaSugerida: {
          maiorQueZero: params.curvaIgualZeroRadio === '> 0',
          igualZero: params.curvaIgualZeroRadio === '= 0'
        },
        quantidadeEstoque: {
          MaiorQueZero: params.quantidadeEstoqueRadio === ' > 0',
          IgualZero: params.quantidadeEstoqueRadio === ' = 0'
        },
        subCategoriasIds: paramsHelper.parse(params.subCategoriaSelectIds)
      }
      const { data: res } = await mrp.getPurschaseCardsData({
        params: paramsToSend
      })
      const { data } = res
      updateDataCards(data)
    } catch (error: any) {
      const message =
        error.response?.status === 400
          ? error.response?.data?.message
          : 'Ocorreu um problema interno. Tente novamente mais tarde'
      toast({
        title: 'Atenção.',
        description: message,
        status: error.response?.status === 400 ? 'warning' : 'error',
        duration: 2000,
        isClosable: true
      })
    }
  }, [params, updateDataCards, setLoading, toast, isUsuarioCompras])

  const exportData = useCallback(async () => {
    Swal.fire({
      title: 'Exportar dados!',
      html: 'Aguarde um momento<b></b>.',
      timerProgressBar: true,
      timer: 99999,
      didOpen: () => {
        Swal.showLoading()
      }
    })
    setLoadingExport(true)

    try {
      const purshase = data
      const command = {
        lojaConfiguracaoGrid: {
          codigo: headers[0].columns[0] ? headers[0].columns[0].show : true,
          nome: headers[0].columns[0] ? headers[0].columns[1].show : true
        },
        centroDistribuicaoConfiguracaoGrid: {
          codigo: false,
          nome: false
        },
        produtoConfiguracaoGrid: {
          material: true,
          leadTime: true,
          intervaloCompra: true,
          codigo: headers[1].columns[0] ? headers[1].columns[0].show : true,
          nome: headers[1].columns[1] ? headers[1].columns[1].show : true,
          categoria: headers[1].columns[4] ? headers[1].columns[4].show : true,
          curvaABC: headers[1].columns[5] ? headers[1].columns[5].show : true,
          curvaABCEmpresa: headers[1].columns[6]
            ? headers[1].columns[6].show
            : true,
          status: headers[1].columns[7] ? headers[1].columns[7].show : true,
          loteMultiplo: headers[1].columns[8]
            ? headers[1].columns[8].show
            : true,
          grupoSeparacao: headers[1].columns[9]
            ? headers[1].columns[9].show
            : true,
          MundoCodigo: headers[1].columns[2]
            ? headers[1].columns[2].show
            : true,
          MundoDescricao: headers[1].columns[2]
            ? headers[1].columns[2].show
            : true,
          CategoriaCompactaDescricao: headers[1].columns[3]
            ? headers[1].columns[3].show
            : true,
          CategoriaCompactaCodigo: headers[1].columns[3]
            ? headers[1].columns[3].show
            : true
        },
        fornecedorConfiguracaoGrid: {
          codigo: headers[2].columns[0] ? headers[2].columns[0].show : true,
          nome: headers[2].columns[1] ? headers[2].columns[1].show : true
        },

        recomendacaoConfiguracaoGrid: {
          pedidos: true,
          coberturaMedia: true,
          quantidade: headers[3].columns[0] ? headers[3].columns[0].show : true,
          peso: headers[3].columns[1] ? headers[3].columns[1].show : true,
          custo: headers[3].columns[3] ? headers[3].columns[3].show : true,
          volume: headers[3].columns[2] ? headers[3].columns[2].show : true,
          tipoAlerta: headers[3].columns[4] ? headers[3].columns[4].show : true,
          nivelServico: headers[3].columns[5]
            ? headers[3].columns[5].show
            : true
        },
        compraConfiguracaoGrid: {
          pedidos: true,
          quantidade: true,
          coberturaMedia: true,
          peso: headers[4].columns[0] ? headers[4].columns[0].show : true,
          volume: headers[4].columns[1] ? headers[4].columns[1].show : true,
          desejada: headers[4].columns[2] ? headers[4].columns[2].show : true,
          custo: headers[4].columns[3] ? headers[4].columns[3].show : true,
          nivelServico: headers[4].columns[4]
            ? headers[4].columns[4].show
            : true
        },

        estoqueConfiguracaoGrid: {
          max: headers[5].columns[0] ? headers[6].columns[0].show : true,
          cd: headers[5].columns[1] ? headers[6].columns[1].show : true,
          reservado: headers[5].columns[2] ? headers[6].columns[2].show : true,

          disponivel: headers[5].columns[3] ? headers[5].columns[3].show : true,
          transito: headers[5].columns[4] ? headers[5].columns[4].show : true,
          dep16: headers[5].columns[5] ? headers[5].columns[5].show : true,
          outros: headers[5].columns[6] ? headers[5].columns[6].show : true,
          vendaMediaDiaria: headers[5].columns[7]
            ? headers[5].columns[7].show
            : true,
          vendaMediaMensal: headers[5].columns[8]
            ? headers[5].columns[8].show
            : true
        },

        estoqueReabEmergencialConfiguracaoGrid: {
          estoqueAtual: true,
          pedidosEmTransito: true,
          coberturaAtual: true,
          curvaABC: true,
          estoqueCD: true,
          quantidadeSugerida: true,
          peso: true
        },

        coberturaConfiguracaoGrid: {
          alvo: headers[6].columns[0] ? headers[6].columns[0].show : true,
          atual: headers[6].columns[1] ? headers[6].columns[1].show : true,
          transito: headers[6].columns[2] ? headers[6].columns[2].show : true,
          sugestao: headers[6].columns[3] ? headers[6].columns[3].show : true,
          desejada: headers[6].columns[4] ? headers[6].columns[4].show : true,
          diferenca: headers[6].columns[5] ? headers[6].columns[5].show : true
        }
      }
      const { data: res } = await mrp.postPurshaseExport({
        params: {
          recomendacaoFornecimentoCommand: purshase,
          configuracaoGridCommand: command
        }
      })
      Swal.close()
      setLoadingExport(false)

      fileDownload(res, filename + '.csv')
    } catch (error: any) {
      setLoadingExport(false)
      Swal.close()

      const message =
        error.response?.status === 400
          ? error.response?.data?.message
          : 'Ocorreu um problema interno. Tente novamente mais tarde'
      toast({
        title: 'Atenção.',
        description: message,
        status: error.response?.status === 400 ? 'warning' : 'error',
        duration: 2000,
        isClosable: true
      })
    }
  }, [data, toast, filename, headers])

  const exportDataSapCsv = useCallback(async () => {
    // VALIDAÇÃO: Quantidade máxima de itens por pedido vs SKUs distintos
    const quantidadeMaximaItens = Number(paramsSap.QuantidadeMaximaItensPedido)
    if (
      quantidadeMaximaItens > 0 &&
      quantidadeMaximaItens > dataCards.skusDistintos
    ) {
      toast({
        title: 'Atenção',
        description: `Quantidade de pedidos (${quantidadeMaximaItens}) é maior que os itens disponíveis para criação (${dataCards.skusDistintos}).`,
        status: 'warning',
        duration: 4000,
        isClosable: true
      })
      return
    }

    Swal.fire({
      title: 'Exporta dados!',
      html: 'Aguarde um momento<b></b>.',
      timerProgressBar: true,
      timer: 99999,
      didOpen: () => {
        Swal.showLoading()
      }
    })
    const purshase = data
    try {
      let obj = {}
      const unique = [
        ...new Set(
          data.map(item => item.estoque?.produtoFornecedor?.fornecedor?.codigo)
        )
      ]
      if (unique.length === 1) {
        obj = {
          recomendacaoFornecimentoCommand: purshase,
          desconto:
            Number(paramsSap.desconto) !== 0
              ? Number(paramsSap.desconto)
              : null,
          formaDePagamento: paramsSap.metodoPagamento[0]
            ? paramsSap.metodoPagamento[0].value !== 0
              ? supplyMethod.find(
                  x => x.id === paramsSap.metodoPagamento[0].value
                )?.codigo ?? null
              : null
            : null,
          quantidadeMaximaItensPedido:
            Number(paramsSap.QuantidadeMaximaItensPedido) !== 0
              ? Number(paramsSap.QuantidadeMaximaItensPedido)
              : null
        }
      } else {
        obj = { recomendacaoFornecimentoCommand: purshase }
      }
      const { data: res } = await mrp.postPurshaseExportSapCsv({
        params: obj
      })
      updateSapParams({
        desconto: '',
        metodoPagamento: [],
        QuantidadeMaximaItensPedido: ''
      })
      fileDownload(res, filename + '.csv')
      Swal.close()
    } catch (error: any) {
      updateSapParams({
        desconto: '',
        metodoPagamento: [],
        QuantidadeMaximaItensPedido: ''
      })
      Swal.close()
      toast({
        title: 'Atenção',
        description: getErrorMessage(error),
        status: 'error',
        duration: 2000,
        isClosable: true
      })
    }
  }, [
    data,
    paramsSap.desconto,
    paramsSap.metodoPagamento,
    paramsSap.QuantidadeMaximaItensPedido,
    dataCards.skusDistintos,
    supplyMethod,
    updateSapParams,
    filename,
    toast
  ])

  const handleLocalParams = useCallback(async () => {
    if (localStorage.getItem('mrpPurchaseParams')) {
      const newParams = JSON.parse(
        crypto.AES.decrypt(
          localStorage.getItem('mrpPurchaseParams'),
          'CHAVE'
        ).toString(crypto.enc.Utf8) || ''
      )
      newParams.utilizarVendaMediaMovel = 'Média Móvel'
      updateParams(newParams)
    } else {
      localStorage.setItem(
        'mrpPurchaseParams',
        crypto.AES.encrypt(JSON.stringify(params), 'CHAVE').toString()
      )
    }
  }, [updateParams, params])

  const handlePageChange = useCallback(
    async (page: number) => {
      setLoading(true)
      await getDataCards()

      try {
        const paramsToSend = {
          ...params,
          categoriasIds: paramsHelper.parse(params.categoriasIds),
          categoriasCompactasIds: paramsHelper.parse(
            params.categoriasCompactasIds
          ),
          produtosIds: paramsHelper.parse(params.produtosIds),
          fornecedoresIds: paramsHelper.parse(params.fornecedoresIds),
          gerenteProdutoIds: paramsHelper.parse(params.gerenteProdutoIds),
          gerenteCategoriaIds: paramsHelper.parse(params.gerenteCategoriaIds),
          mundoIds: paramsHelper.parse(params.mundoIds),
          lojasIds: paramsHelper.parse(params.lojasIds),
          curvaAbc: params.curvaAbc.length > 0 ? params.curvaAbc[0].value : '',
          centerTypes: paramsHelper.parse(params.centerTypes),
          subCategoriasIds: paramsHelper.parse(params.subCategoriaSelectIds)
        }
        const { data: res } = await mrp.getPurschaseData({
          page: page,
          params: paramsToSend
        })

        const { data, pagination } = res

        updateData(draft => {
          return draft.filter(item => item.status === 99).concat(data)
        })

        updatePagination(pagination)
      } catch {}
      setLoading(false)
    },
    [params, updateData, getDataCards, updatePagination]
  )

  const getDiscountBySupply = useCallback(async () => {
    try {
      const { data: res } = await generic.getDiscountSupply({
        fornecedoresIds: [
          params.fornecedoresIds.length > 0
            ? params.fornecedoresIds[0].value
            : 0
        ]
      })

      const { data } = res

      updateDiscountSupply(data[0])
      // const newParamsSap = paramsSap
      // updateSapParams({ ...newParamsSap, desconto: data[0].desconto })
    } catch {}
  }, [updateDiscountSupply, params])

  const calculate = useCallback(
    async (data: any[]) => {
      const distinctSkus: any[] = []
      const idsFornecedor: any = []
      let customRecomendadoSum = 0
      let custoTotalDeCompraSum = 0
      let volumeTotalDeCompra = 0

      for (const row of data) {
        customRecomendadoSum += row.valorRecomendacao
          ? (row.valorRecomendacao.quantidade
              ? row.valorRecomendacao.quantidade
              : 0) *
            (row.estoque.custoUnitario ? row.estoque.custoUnitario : 0) *
            (row.estoque.produtoFornecedor.unidadeFornecedor.fatorConversao
              ? row.estoque.produtoFornecedor.unidadeFornecedor.fatorConversao
              : 0)
          : 0

        custoTotalDeCompraSum += row.desejada
          ? (row.desejada.quantidade ? row.desejada.quantidade : 0) *
            (row.estoque.custoUnitario ? row.estoque.custoUnitario : 0) *
            (row.estoque.produtoFornecedor.unidadeFornecedor.fatorConversao
              ? row.estoque.produtoFornecedor.unidadeFornecedor.fatorConversao
              : 0)
          : 0

        volumeTotalDeCompra += row.desejada
          ? row.desejada.volume
            ? row.desejada.volume
            : 0
          : 0

        const checkOne = !!distinctSkus.filter(
          x =>
            x.supplier ===
              (row.estoque.produtoFornecedor.fornecedor
                ? row.estoque.produtoFornecedor.fornecedor.id
                : 0) &&
            x.product ===
              (row.estoque.produtoFornecedor.produto
                ? row.estoque.produtoFornecedor.produto.id
                : 0)
        )
        const checkTwo = row.valorRecomendacao.quantidade > 0

        if (checkOne && checkTwo) {
          idsFornecedor.push(row.estoque.id)
          distinctSkus.push({
            supplier: row.estoque.produtoFornecedor.fornecedor
              ? row.estoque.produtoFornecedor.fornecedor.id
              : 0,
            product: row.estoque.produtoFornecedor.produto
              ? row.estoque.produtoFornecedor.produto.id
              : 0
          })
        }
      }

      updateDataCards({
        custo: custoTotalDeCompraSum,
        skusDistintos: distinctSkus.length,
        volume: volumeTotalDeCompra,
        custoRecomendado: customRecomendadoSum,
        cards: []
      })
    },
    [updateDataCards]
  )

  const handleUpdateData = useCallback(
    (data: any[]) => {
      setLoading(true)
      updateData(draft => {
        return draft.filter(item => item.status === 99).concat(data)
      })
      calculate(data)
      setLoading(false)
    },
    [updateData, calculate]
  )

  // Busca a recomendação com os parâmetros informados. `isReload` é usado para
  // recarregar após o envio do pedido, sem alternar o painel de filtros.
  const fetchData = useCallback(
    async (searchParams: IParams, isReload = false) => {
      if (
        isUsuarioCompras &&
        !getUtilizarCronogramaValue(searchParams.utilizarCronograma)
      ) {
        toast({
          title: 'Atenção',
          description: 'Realizar a seleção do Utilizar Cronograma',
          status: 'warning',
          duration: 2000,
          isClosable: true
        })
        return
      }

      // Cronograma só vale para o usuário compras; os demais seguem o fluxo
      // anterior (sempre exigindo ao menos um filtro)
      const isUtilizarCronogramaAtivo =
        isUsuarioCompras &&
        getUtilizarCronogramaValue(searchParams.utilizarCronograma) === '1'

      if (!isUtilizarCronogramaAtivo) {
        const isDisabledProgress =
          searchParams.lojasIds.length > 0 ||
          searchParams.categoriasCompactasIds.length > 0 ||
          searchParams.categoriasIds.length > 0 ||
          searchParams.curvaAbc.length > 0 ||
          searchParams.fornecedoresIds.length > 0 ||
          searchParams.mundoIds.length > 0

        if (!isDisabledProgress) {
          toast({
            title: 'Atenção',
            description: 'Selecionar ao menos um filtro',
            status: 'warning',
            duration: 2000,
            isClosable: true
          })
          return
        }
      }
      setAlteredSkus([])
      pendingWantedRef.current.clear()
      setLoading(true)
      try {
        const buffersId: any[] = []
        const utilizarVendaMediaMovel =
          !!vmmFlag && searchParams.utilizarVendaMediaMovel === 'Média Móvel'

        const paramsToSend = {
          utilizarCronogramaCompras: isUtilizarCronogramaAtivo,
          tiposAlertas: paramsHelper.parse(searchParams.tiposAlertas),
          categoriasIds: paramsHelper.parse(searchParams.categoriasIds),
          categoriasCompactasIds: paramsHelper.parse(
            searchParams.categoriasCompactasIds
          ),
          produtosIds: paramsHelper.parse(searchParams.produtosIds),
          fornecedoresIds: paramsHelper.parse(searchParams.fornecedoresIds),
          gerenteProdutoIds: paramsHelper.parse(searchParams.gerenteProdutoIds),
          gerenteCategoriaIds: paramsHelper.parse(
            searchParams.gerenteCategoriaIds
          ),
          mundoIds: paramsHelper.parse(searchParams.mundoIds),
          lojasIds: paramsHelper.parse(searchParams.lojasIds),
          curvaAbc:
            searchParams.curvaAbc.length > 0
              ? searchParams.curvaAbc[0].value
              : '',
          tiposCentros: paramsHelper.parse(searchParams.centerTypes),
          subCategoriasIds: paramsHelper.parse(
            searchParams.subCategoriaSelectIds
          ),
          quantidadeDesejadaSugerida: {
            maiorQueZero: searchParams.curvaIgualZeroRadio === '> 0',
            igualZero: searchParams.curvaIgualZeroRadio === '= 0'
          },
          quantidadeEstoque: {
            MaiorQueZero: searchParams.quantidadeEstoqueRadio === ' > 0',
            IgualZero: searchParams.quantidadeEstoqueRadio === ' = 0'
          },
          utilizarVendaMediaMovel
        }

        setLastSearchVmm(utilizarVendaMediaMovel)
        const { data: res } = await mrp.getPurschaseData({
          page: 1,
          params: paramsToSend
        })

        const { data, pagination, message } = res

        updatePossuirBuffer(buffersId)
        updateData(draft =>
          draft.filter(item => item.status === 99).concat(data)
        )
        updatePagination(pagination)
        setAppliedParams({ ...searchParams })
        setLoading(false)

        if (searchParams.fornecedoresIds.length === 1) {
          getDiscountBySupply()
        }

        calculate(data)
        if (!isReload) setFilters.toggle()

        if (message === 'ProdutosNaoParametrizados') {
          toast({
            title: 'Atenção',
            description:
              'Alguns produtos não trouxeram resultado por não estarem parametrizados corretamente',
            status: 'error',
            duration: 6000,
            isClosable: true
          })
        }

        if (!isReload) {
          register({
            name: 'MRP Compra',
            path: '/mrp/compra',
            loading: false
          })
        }
      } catch (e: any) {
        const { status } = e.response ?? {}
        setLoading(false)
        if (!isReload) setFilters.toggle()
        toast({
          title: 'Atenção',
          description: getErrorMessage(e),
          status: status === 400 ? 'warning' : 'error',
          duration: 2000,
          isClosable: true
        })

        updateData([])

        updateDataCards({
          skusDistintos: 0,
          volume: 0,
          custo: 0,
          custoRecomendado: 0,
          cards: []
        })
      }
    },
    [
      isUsuarioCompras,
      vmmFlag,
      setAlteredSkus,
      toast,
      updatePossuirBuffer,
      updateData,
      updatePagination,
      calculate,
      setFilters,
      register,
      getDiscountBySupply,
      updateDataCards
    ]
  )

  const getData = useCallback(() => fetchData(params), [fetchData, params])

  const exportDataSap = useCallback(async (): Promise<boolean> => {
    // Trava contra envio duplicado (duplo clique / Enter repetido)
    if (isSubmittingRef.current) return false

    // Usar os params que foram aplicados no último filtro, não os atuais
    const activeParams = appliedParams || params

    // Validação: pelo menos uma recomendação com Quantidade Desejada > 0
    const linhasValidasParaEnvio = data.filter(
      item => item.desejada?.quantidade > 0
    )
    if (linhasValidasParaEnvio.length === 0) {
      toast({
        title: 'Atenção',
        description:
          'Nenhuma linha com "Compra Desejada" maior que zero foi encontrada para exportação.',
        status: 'warning',
        duration: 4000,
        isClosable: true
      })
      return false
    }

    isSubmittingRef.current = true
    setSendingOrder(true)

    // Envia apenas as linhas válidas, com justificativa/aprovador dos SKUs alterados
    const alteredById = new Map(alteredSkus.map(sku => [sku.id, sku]))
    const purshase = linhasValidasParaEnvio.map(item => {
      const alteredSku = alteredById.get(item.estoque.id)
      if (alteredSku) {
        return {
          ...item,
          justificativa: alteredSku.justificativa,
          observacao: alteredSku.observacao,
          usuarioAprovadorEmail: alteredSku.usuarioAprovadorEmail
        }
      }
      return item
    })

    Swal.fire({
      title: 'Criando Pedidos!',
      html: 'Aguarde um momento<b></b>.',
      allowOutsideClick: false,
      allowEscapeKey: false,
      allowEnterKey: false,
      showConfirmButton: false,
      backdrop: true,
      didOpen: () => {
        Swal.showLoading()
      }
    })

    const isUtilizarCronogramaAtivo =
      isUsuarioCompras &&
      getUtilizarCronogramaValue(activeParams.utilizarCronograma) === '1'

    // Só vai quando o cronograma está desligado; o backend ignora para quem não é Comprador
    const justificativaForaCronogramaPayload = isUtilizarCronogramaAtivo
      ? null
      : justificativaForaCronograma.trim() || null

    try {
      let obj = {}
      const unique = [
        ...new Set(
          data.map(item => item.estoque?.produtoFornecedor?.fornecedor?.codigo)
        )
      ]

      if (unique.length === 1) {
        obj = {
          quantidadeMaximaItensPedido:
            Number(paramsSap.QuantidadeMaximaItensPedido) !== 0
              ? Number(paramsSap.QuantidadeMaximaItensPedido)
              : null,
          recomendacaoFornecimentoCommand: purshase,
          desconto:
            Number(paramsSap.desconto) !== 0
              ? Number(paramsSap.desconto)
              : null,
          formaDePagamento: paramsSap.metodoPagamento[0]
            ? paramsSap.metodoPagamento[0].value !== 0
              ? supplyMethod.find(
                  x => x.id === paramsSap.metodoPagamento[0].value
                )?.codigo ?? null
              : null
            : null,
          utilizarCronogramaCompras: isUtilizarCronogramaAtivo,
          justificativaForaCronograma: justificativaForaCronogramaPayload
        }
      } else {
        obj = {
          quantidadeMaximaItensPedido:
            Number(paramsSap.QuantidadeMaximaItensPedido) !== 0
              ? Number(paramsSap.QuantidadeMaximaItensPedido)
              : null,
          recomendacaoFornecimentoCommand: purshase,
          utilizarCronogramaCompras: isUtilizarCronogramaAtivo,
          justificativaForaCronograma: justificativaForaCronogramaPayload
        }
      }
      const { data: res, status } = await mrp.postPurshaseExportSap({
        params: obj
      })
      if (status !== 200 || res?.erro || res?.error || res?.statusCode >= 400) {
        throw new Error(res?.message || 'Erro ao processar pedido')
      }

      setCurrentOrderCode('103')
      updateSapParams({
        desconto: '',
        metodoPagamento: [],
        QuantidadeMaximaItensPedido: ''
      })

      // Usar mensagem do backend se disponível, senão usar mensagem padrão
      const successMessage =
        res?.message || res?.data?.message || 'Pedido Criado com Sucesso'
      setOrderSuccessMessage(successMessage)

      // Itens já enviados: limpa alterações e justificativa e recarrega a
      // recomendação para que os mesmos itens não sejam reenviados
      setAlteredSkus([])
      setJustificativaForaCronograma('')

      Swal.close()
      toast({
        title: 'Sucesso',
        description: successMessage,
        status: 'success',
        duration: 5000,
        isClosable: true
      })
      fetchData(activeParams, true)
      return true
    } catch (error: any) {
      // Mantém forma de pagamento/desconto para o usuário poder tentar de novo
      Swal.close()
      toast({
        title: 'Atenção',
        description: getErrorMessage(error),
        status: 'error',
        duration: 4000,
        isClosable: true
      })
      return false
    } finally {
      isSubmittingRef.current = false
      setSendingOrder(false)
    }
  }, [
    data,
    paramsSap.desconto,
    paramsSap.metodoPagamento,
    paramsSap.QuantidadeMaximaItensPedido,
    supplyMethod,
    updateSapParams,
    setAlteredSkus,
    toast,
    alteredSkus,
    appliedParams,
    params,
    isUsuarioCompras,
    justificativaForaCronograma,
    fetchData
  ])

  const getSupplyCommands = useCallback(async () => {
    try {
      const { data: res } = await generic.getSupplyMethods()

      const { data } = res

      updateSupplyMethod(data)
    } catch {}
    setLoading(false)
  }, [updateSupplyMethod])

  // Substitui um item da recomendação pelo id do estoque (mantém o ref em dia
  // para chamadas assíncronas que chegam antes do próximo render)
  const replacePurchaseItem = useCallback(
    (estoqueId: number, item: any) => {
      dataRef.current = dataRef.current.map(row =>
        row.estoque?.id === estoqueId ? item : row
      )
      updateData(draft => {
        const index = draft.findIndex(row => row.estoque?.id === estoqueId)
        if (index !== -1) draft[index] = item
      })
    },
    [updateData]
  )

  const handleUpdateWanted = useCallback(
    async (index: number, wanted: number | string) => {
      const estoqueId = Number(index)
      const quantidade = Number(String(wanted ?? '').replace(/\.|\-/g, ''))
      if (Number.isNaN(quantidade)) return

      const current = dataRef.current.find(row => row.estoque?.id === estoqueId)
      if (!current) return

      // Ignora quando não há mudança (ex.: blur logo após Enter) ou o mesmo valor já está em envio
      const pending = pendingWantedRef.current.get(estoqueId)
      const quantidadeAtual = pending
        ? pending.quantidade
        : Number(current.desejada?.quantidade ?? 0)
      if (quantidadeAtual === quantidade) return

      // Item antes de qualquer edição em andamento, para restaurar em caso de erro
      const originalItem = pending ? pending.original : current
      pendingWantedRef.current.set(estoqueId, {
        quantidade,
        original: originalItem
      })

      const optimisticItem = current.desejada
        ? {
            ...current,
            desejada: { ...current.desejada, quantidade }
          }
        : current
      replacePurchaseItem(estoqueId, optimisticItem)

      const isLatest = () =>
        pendingWantedRef.current.get(estoqueId)?.quantidade === quantidade

      try {
        const {
          data: { data: updatedSkuData }
        } = await mrp.putPurshaseWanted({
          page: 1,
          params: {
            quantidadeDesejada: quantidade.toString(),
            recomendacaoFornecimentoCommand: optimisticItem
          }
        })

        // Uma edição mais recente do mesmo SKU já foi disparada: descarta esta resposta
        if (!isLatest()) return

        replacePurchaseItem(estoqueId, updatedSkuData)
        setAlteredSkus(draft => {
          upsertAlteredSku(draft, buildPurchaseAlteredSku(updatedSkuData))
        })
        calculate(dataRef.current)
      } catch (error: any) {
        toast({
          title: 'Atenção',
          description: getErrorMessage(error),
          status: error?.response?.status === 400 ? 'warning' : 'error',
          duration: 2000,
          isClosable: true
        })
        if (!isLatest()) return

        // 400 com o item recalculado pelo backend: aplica; qualquer outro erro restaura o original
        const backendItem =
          error?.response?.status === 400 ? error.response?.data?.data : null
        if (backendItem?.estoque) {
          replacePurchaseItem(estoqueId, backendItem)
          setAlteredSkus(draft => {
            upsertAlteredSku(draft, buildPurchaseAlteredSku(backendItem))
          })
        } else {
          replacePurchaseItem(estoqueId, originalItem)
        }
        calculate(dataRef.current)
      } finally {
        if (isLatest()) pendingWantedRef.current.delete(estoqueId)
      }
    },
    [replacePurchaseItem, calculate, toast, setAlteredSkus]
  )

  const handleUpdateJustification = useCallback(
    (skuId: number, justification: string, observation: string) => {
      setAlteredSkus(draft => {
        const sku = draft.find(item => item.id === skuId)
        if (sku) {
          sku.justificativa = justification
          sku.observacao = observation
        }
      })
    },
    [setAlteredSkus]
  )

  const handleBulkUpdateApprovers = useCallback(
    (approversMap: Map<number, string>) => {
      setAlteredSkus(draft => {
        draft.forEach(sku => {
          if (approversMap.has(sku.id)) {
            sku.usuarioAprovadorEmail = approversMap.get(sku.id)
          }
        })
      })
    },
    [setAlteredSkus]
  )

  const postUpload = useCallback(
    async (event: any) => {
      setLoading(true)

      const previousData = data

      // A planilha altera quantidades desejadas: recalcula os SKUs alterados
      // (desejada x recomendada) para exigir justificativa/aprovação
      const applyUploadedData = (uploaded: any[]) => {
        updateData(draft => {
          return draft.filter(item => item.status === 99).concat(uploaded)
        })
        calculate(uploaded)

        const previousById = new Map(
          previousData.map(item => [item.estoque?.id, item])
        )
        const uploadedIds = new Set(uploaded.map(item => item?.estoque?.id))
        setAlteredSkus(draft => {
          for (let i = draft.length - 1; i >= 0; i--) {
            if (!uploadedIds.has(draft[i].id)) draft.splice(i, 1)
          }
          uploaded.forEach(item => {
            if (!item?.estoque) return
            const previous = previousById.get(item.estoque.id)
            if (
              previous &&
              Number(previous.desejada?.quantidade ?? 0) ===
                Number(item.desejada?.quantidade ?? 0)
            ) {
              return
            }
            upsertAlteredSku(draft, buildPurchaseAlteredSku(item))
          })
        })
      }

      try {
        const { data: res } = await mrp.uploadPurchase({
          file: event.target.files[0],
          params: previousData
        })

        if (Array.isArray(res?.data)) {
          applyUploadedData(res.data)
        }
      } catch (error: any) {
        toast({
          title: 'Atenção',
          description: getErrorMessage(error),
          status: error?.response?.status === 400 ? 'warning' : 'error',
          duration: 2000,
          isClosable: true
        })
        const dataToUse =
          error?.response?.status === 400 ? error.response?.data?.data : null
        if (Array.isArray(dataToUse)) {
          applyUploadedData(dataToUse)
        }
      } finally {
        setLoading(false)
      }
    },
    [toast, setLoading, data, updateData, calculate, setAlteredSkus]
  )

  const getBuffer = useCallback(
    async (storeId: number, productId: number) => {
      setBufferModalCentroId(storeId)
      setBufferModalProdutoId(productId)
      setBufferModalOpen(true)
      setLoadingBufferModal(true)

      generic
        .getBuffer(productId, storeId)
        .then(({ data: { data } }) => {
          updateBuffers(data.buffers)
          updateCentroBuffer(data.centro)

          updateBufferFooter({
            disponivel: data.totalDisponivel,
            reservado: data.totalReservado
          })
          setLoadingBufferModal(false)
        })
        .catch(() => {
          updateBuffers([])
          updateCentroBuffer({})
          updateBufferFooter({
            disponivel: 0,
            reservado: 0
          })
          setLoadingBufferModal(false)
        })
    },
    [updateBuffers, updateCentroBuffer, updateBufferFooter]
  )

  useEffect(() => {
    const fetchSapFlag = async () => {
      const flag = await isSapExportFlag()
      setSapFlag(flag)
    }
    const fetchVmmFlag = async () => {
      const flag = await isVmmFlag()
      setVmmFlag(flag)
    }
    fetchSapFlag()
    fetchVmmFlag()
  }, [])

  // Parse ALL data once (just JS transformation — fast)
  // Sort and pagination happen in TableGrid on the full set
  const parsedData = useMemo(() => parsePurchaseMrpData(data), [data])

  const totalCount = data.length

  const contextValue = useMemo(
    () => ({
      loading,
      fetching,
      postUpload,
      paramsSap,
      discountSupply,
      filtersList,
      tableInteraction,

      clearAllFilters,

      data: parsedData,
      dataCards,
      actions,
      params,
      appliedParams,
      calc,
      handlePageChange,
      pagination,

      headers,
      headerLocalStorange,
      selected,
      filename,
      query,
      filters,

      toggleColumn,
      handleUpdateParams,

      getData,
      handleUpdateData,
      getDataCards,
      exportData,
      exportDataSap,

      handleUpdateSapParams,
      handleUpdateWanted,
      getSupplyCommands,
      getDiscountBySupply,
      handleLocalParams,
      supplyMethod,
      setSelected,
      setQuery,
      setFilters,
      handleLoading,
      setFilename,
      buffers,
      centroBuffer,
      getBuffer,
      possuirBuffer,
      bufferFooter,
      loadingBufferModal,
      exportDataSapCsv,
      lastSearchVmm,
      totalCount,
      alteredSkus,
      alteredSkuIds,
      sendingOrder,
      handleUpdateJustification,
      handleBulkUpdateApprovers,
      justificativaForaCronograma,
      setJustificativaForaCronograma
    }),
    [
      loading,
      fetching,
      postUpload,
      paramsSap,
      discountSupply,
      filtersList,
      tableInteraction,
      clearAllFilters,
      parsedData,
      dataCards,
      actions,
      params,
      calc,
      handlePageChange,
      pagination,
      headers,
      headerLocalStorange,
      selected,
      filename,
      query,
      filters,
      toggleColumn,
      handleUpdateParams,
      getData,
      handleUpdateData,
      getDataCards,
      exportData,
      exportDataSap,
      handleUpdateSapParams,
      handleUpdateWanted,
      getSupplyCommands,
      getDiscountBySupply,
      handleLocalParams,
      supplyMethod,
      setSelected,
      setQuery,
      setFilters,
      handleLoading,
      setFilename,
      buffers,
      centroBuffer,
      getBuffer,
      possuirBuffer,
      bufferFooter,
      loadingBufferModal,
      exportDataSapCsv,
      lastSearchVmm,
      totalCount,
      alteredSkus,
      alteredSkuIds,
      sendingOrder,
      handleUpdateJustification,
      handleBulkUpdateApprovers,
      justificativaForaCronograma,
      appliedParams
    ]
  )

  return (
    <MrpPurchaseContext.Provider value={contextValue}>
      {children}
      {bufferModalOpen && (
        <Modal
          isCentered
          isOpen={bufferModalOpen}
          onClose={() => setBufferModalOpen(false)}
          scrollBehavior="inside"
          motionPreset="slideInBottom"
          colorScheme="white"
          size="6xl"
        >
          <ModalOverlay />
          <ModalContent
            background={'white'}
            marginLeft={[4, 8, 16]}
            marginRight={[4, 8, 16]}
          >
            <ModalHeader
              paddingTop={2}
              paddingBottom={2}
              paddingLeft={4}
              paddingRight={4}
              textColor={'gray.200'}
              background={'#1d5840'}
              borderTopLeftRadius={5}
              borderTopRightRadius={5}
              borderBottom="3px solid #ECC94B"
            >
              <Flex alignItems="center">
                <Text marginLeft={2}>Função Buffer</Text>
              </Flex>
            </ModalHeader>
            <ModalCloseButton
              top={1}
              right={1.5}
              textColor={'gray.200'}
              size="lg"
            />
            <ModalBody bg="white">
              {loadingBufferModal ? (
                <LayoutLoader
                  type="modal"
                  title="Recuperando informações..."
                  message="Aguarde enquanto buscamos os registros no banco de dados"
                />
              ) : buffers.length > 0 ? (
                <Box
                  p={4}
                  pt={6}
                  pb={6}
                  pos="relative"
                  maxW="100%"
                  overflow="auto"
                >
                  <Table size="sm" className="scl-table-grid">
                    <Thead>
                      <Tr>
                        <Th>Centro</Th>
                        <Th>Tipo centro</Th>
                        <Th>Produto Codigo</Th>
                        <Th>Produto Material</Th>
                        <Th>Disponivel</Th>
                        <Th>Reservado</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      <Tr>
                        <Td>{centroBuffer.centro}</Td>
                        <Td>CENTRO</Td>
                        <Td>{centroBuffer.produtoId}</Td>
                        <Td>{centroBuffer.produtoMaterial}</Td>
                        <Td>{centroBuffer.disponivel}</Td>
                        <Td>{centroBuffer.reservado}</Td>
                      </Tr>
                      {buffers.map((value, idx) => (
                        <Tr key={idx}>
                          <Td>{value.centro}</Td>
                          <Td>BUFFER</Td>
                          <Td>{value.produtoId}</Td>
                          <Td>{value.produtoMaterial}</Td>
                          <Td>{value.disponivel}</Td>
                          <Td>{value.reservado}</Td>
                        </Tr>
                      ))}
                    </Tbody>
                    <Tfoot fontWeight={'black.500'}>
                      <Tr>
                        <Th fontSize={14} color={'black'}>
                          Total:
                        </Th>
                        <Th></Th>
                        <Th></Th>
                        <Th></Th>
                        <Th fontSize={14} color={'black'}>
                          {bufferFooter.disponivel.toLocaleString('pt-BR')}
                        </Th>
                        <Th fontSize={14} color={'black'}>
                          {bufferFooter.reservado.toLocaleString('pt-BR')}
                        </Th>
                      </Tr>
                    </Tfoot>
                  </Table>
                </Box>
              ) : (
                <Box>
                  <Flex
                    flexDir="column"
                    alignItems="center"
                    justifyContent="center"
                    bg="white"
                    borderRadius="8px"
                    h="calc(30vh - 128px)"
                    p={8}
                  >
                    <Text maxW="460px" fontSize={18} textAlign="center">
                      Não existe registro de buffers para o centro{' '}
                      {bufferModalCentroId} e o produto {bufferModalProdutoId}.
                    </Text>
                  </Flex>
                </Box>
              )}
            </ModalBody>
          </ModalContent>
        </Modal>
      )}
      <SapHistoricModalWrapper
        orderCode={currentOrderCode}
        onModalClosed={handleClearOrderCode}
        successMessage={orderSuccessMessage}
      />
      <MrpPeriodChart
        title="Compra"
        list={data}
        selected={selected}
        setSelected={setSelected}
      />
    </MrpPurchaseContext.Provider>
  )
}

export default MrpPurchaseProvider
export const MrpPurchaseContext = createContext<IMrpPurchaseContext>(
  {} as IMrpPurchaseContext
)
